import { serpSeo } from '@/config/serpSeo';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug, SearchType } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { _t } from '@/locale';
import type { TranslationKey } from '@/locale/translation-map';
import { FILTER_ID } from '@/types/state/search';
import { jsUcfirst } from './objects';
import { getLocationPreposition } from './seo';
import { capitalizeFirstLetter } from './strings';
import { decodeURIComponentSafe } from './urlTs';

function getSerpPageTitle(search: BaseSearchManagerOutput): string {
  const title = [];
  if (search && search.q) {
    title.push(search.q);
  } else {
    title.push(_t('BeautyAndHealth'));
  }

  if (search && search.location) {
    title.push(jsUcfirst(search.location));
  } else {
    title.push(_t('allSweden'));
  }

  return jsUcfirst(title.join(' '));
}

export function getSerpSeoTitle(search: BaseSearchManagerOutput): string {
  const parts = [];

  if (search.type === 'sistaminuten') {
    parts.push('Billig ');
  } else if (search.type === 'deals' || search.type === 'deals_preview') {
    parts.push('Rabatt ');
  }

  if (search.page > 0) {
    const urlPageNumber = search.page + 1;
    parts.unshift(_t('seo.page', { page: urlPageNumber }));
  }

  const pageTitle = getSerpPageTitle(search);
  /**
   * add additional separation between title and location to avoid using
   * existing company names for SEO (e.g. Terapi Falun)
   */
  if (pageTitle.toLowerCase() === 'terapi falun') {
    parts.push(`Terapi ${_t('in')} Falun`);
  } else {
    parts.push(getSerpPageTitle(search));
  }

  if (search.type === 'sistaminuten') {
    parts.push(' | Boka Sista Minuten');
  } else {
    /**
     * if page is a predefined category page and has a custom CTA title, append it to the title
     * otherwise append the default - Bokadirekt
     */
    const keyword = decodeURIComponentSafe(search.q);
    const cta = getCategoryTitleCTA(keyword, search.type);

    const isDeals = search.type === 'deals' || search.type === 'deals_preview';
    const fallback = isDeals ? ' - Deals på Bokadirekt' : ' - Bokadirekt';

    parts.push(cta ? ` - ${cta}` : fallback);
  }

  return parts.join('');
}

export function getSerpSeoDescription(search: BaseSearchManagerOutput): string {
  const keyword = decodeURIComponentSafe(search.q);
  const location = decodeURIComponentSafe(search.location);

  const customDescription = getCategoryDescription(search.type, keyword, location);

  if (customDescription) {
    return customDescription;
  }

  let generalDescription = getSerpPageTitle(search);

  switch (search.type) {
    case 'sistaminuten':
    case 'deals_sistaminuten':
      generalDescription += _t('seo.sistaminuten.serpDescription');
      break;
    case 'deals':
    case 'deals_preview':
      generalDescription += _t('seo.deals.serpDescription');
      break;
    default:
      generalDescription += _t('seo.serpDescription');
  }

  return generalDescription;
}

export function getCategoryDescription(
  type: SearchType,
  q?: string,
  location = DEFAULT_SEARCH_LOCATION,
): string | null {
  /**
   * hard code brand deals description for now
   */
  if (type === 'brand_deals') {
    return _t('seo.brandDealsDescription', { brandName: q });
  } else if (type === 'deals_sistaminuten') {
    return _t('seo.dealsSistaMinutenDescription');
  }

  const slug = getKeywordWithSlug(q)?.slug;
  const loc = getLocationWithSlug(location);

  if (!slug || !loc) return null;

  const entry = serpSeo.find((e) => e.slugs.includes(slug));

  if (!entry) return null;

  let key: TranslationKey = entry.descriptionKey;

  if (type === 'deals' || type === 'deals_preview') {
    key = `${entry.descriptionKey}.deals` as TranslationKey;
  } else if (type === 'sistaminuten') {
    key = `${entry.descriptionKey}.sistaminuten` as TranslationKey;
  }

  let preposition = getLocationPreposition(location);
  let city = loc.label;

  if (loc.slug === 'nara-mig') {
    city = _t('fakeSearchNearWithoutPreposition');
  } else if (loc.slug === 'hela-sverige') {
    city = _t('allSweden');
  }

  return (_t(key) as string).replace('$$preposition$$', preposition).replace('$$city$$', city);
}

export function getCategoryTitleCTA(keyword?: string, type?: SearchType): string | null {
  if (!keyword) return null;

  const slug = getKeywordWithSlug(keyword)?.slug;

  if (!slug) return null;

  const entry = serpSeo.find((e) => e.slugs.includes(slug));

  if (!entry) return null;

  if (type === 'deals' || type === 'deals_preview') {
    const suffixedKey = `${entry.ctaKey}.deals` as TranslationKey;
    const translated = _t(suffixedKey) as string;
    if (translated !== suffixedKey) return translated;
  } else if (type === 'sistaminuten' || type === 'deals_sistaminuten') {
    const suffixedKey = `${entry.ctaKey}.sistaminuten` as TranslationKey;
    const translated = _t(suffixedKey) as string;
    if (translated !== suffixedKey) return translated;
  }

  return _t(entry.ctaKey) as string;
}

export function getSearchResultsPageHeadingProps(search: BaseSearchManagerOutput) {
  let keywordLabel: string = undefined;
  let locationLabel: string = undefined;

  const indexableKeyword = getKeywordWithSlug(search.q);
  const indexableLocation = getLocationWithSlug(search.location);

  if (indexableKeyword && indexableKeyword.slug !== DEFAULT_SEARCH_KEYWORD) {
    keywordLabel = indexableKeyword.label;
  } else {
    keywordLabel = capitalizeFirstLetter(decodeURIComponentSafe(search.q));
  }

  if (indexableLocation) {
    locationLabel = indexableLocation.label;
  } else {
    locationLabel = capitalizeFirstLetter(decodeURIComponentSafe(search.location));
  }

  /**
   * For deals release dont change the existing description for legacy sistaminuten search
   */

  const _type = search.type === 'sistaminuten' ? 'default' : search.type;

  let description = getCategoryDescription(
    _type,
    search.q,
    search.location && search.location.length > 0 ? search.location : undefined,
  );

  try {
    // Remove "lymfmassage" when keyword is "massage" and gift card filter is active
    const isGiftCardFilterOn = (search.prefs ?? '').split(',').filter(Boolean).map(Number).includes(FILTER_ID.GIFTCARD);

    const isKeywordMassage = indexableKeyword?.slug === 'massage';

    if (isKeywordMassage && isGiftCardFilterOn && description?.includes('lymfmassage')) {
      description = description.replace('lymfmassage', '');
    }
  } catch (e) {}

  let title = `${keywordLabel || _t('Beauty&Health')}`;

  if (!locationLabel) {
    title += ` ${_t('AllSweden')}`;
  } else if (indexableLocation?.slug !== DEFAULT_MY_LOCATION_KEYWORD) {
    title += ` ${locationLabel}`;
  } else {
    title += ` ${_t('fakeSearchNear')}`;
  }

  return {
    title,
    keyword: `${keywordLabel || _t('Beauty&Health')}`,
    location: locationLabel || _t('AllSweden'),
    description: description,
  };
}
